/**
 * InstagramMetricsPage Component (Admin_Season_Leaderboard_Page)
 *
 * Admin page displaying a season-scoped Instagram leaderboard. Shows the
 * current season by default and lets the admin pick any prior season that
 * has at least one baseline.
 *
 * Season metrics are computed server-side as `max(0, current - baseline)` for
 * each of `followersGained`, `postsMadeThisSeason`, `reachThisSeason`, and
 * `impressionsThisSeason`. A hero line chart shows the top-5 contestants on
 * the currently active metric, and each row carries an inline sparkline.
 *
 * Requirements: 6.1 - 6.17
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { listSeasonLeaderboard, listSeasonGrowthSeries } from '@/graphql/queries';
import { ChevronDown, ChevronUp, BarChart3, AlertTriangle } from 'lucide-react';
import CrownLoader from '@/components/ui/CrownLoader';
import { DataTable } from '@/components/ui/data-table';
import { SeasonGrowthSparkline } from '@/components/charts/SeasonGrowthSparkline';
import { SeasonGrowthHeroChart } from '@/components/charts/SeasonGrowthHeroChart';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import {
  USE_MOCK_SPOTLIGHT,
  MOCK_SEASON_LEADERBOARD,
  MOCK_GROWTH_SERIES,
} from '@/lib/spotlight/mockData';

/** Instagram camera icon — matches the icon used in InstagramLinkButton. */
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  );
}

const client = generateClient();

// ─── Types ──────────────────────────────────────────────────────────────────

interface SeasonLeaderboardEntry {
  cognitoSub: string;
  fullName: string;
  instagramUsername: string;
  followersGained: number;
  postsMadeThisSeason: number;
  reachThisSeason: number;
  impressionsThisSeason: number;
  baselineCapturedAt: string;
  lastSnapshotAt: string | null;
}

interface SparklinePoint {
  snapshotDate: string;
  value: number;
}

interface GrowthSeriesItem {
  cognitoSub: string;
  fullName: string;
  instagramUsername: string;
  points: SparklinePoint[];
}

type SortColumn =
  | 'followersGained'
  | 'postsMadeThisSeason'
  | 'reachThisSeason'
  | 'impressionsThisSeason';

type SortDirection = 'asc' | 'desc';

type HeroMetric = 'followersGained' | 'reachThisSeason' | 'postsMadeThisSeason';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Column Definitions ─────────────────────────────────────────────────────

interface ColumnDef {
  key: SortColumn;
  label: string;
}

const METRIC_COLUMNS: ColumnDef[] = [
  { key: 'followersGained', label: 'Followers Gained' },
  { key: 'postsMadeThisSeason', label: 'Posts' },
  { key: 'reachThisSeason', label: 'Reach' },
  { key: 'impressionsThisSeason', label: 'Impressions' },
];

// ─── Sort Indicator ─────────────────────────────────────────────────────────

function SortIndicator({
  column,
  activeColumn,
  direction,
}: {
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
}) {
  if (column !== activeColumn) {
    return <ChevronDown className="w-3 h-3 text-gold-300 ml-1 inline-block" />;
  }
  return direction === 'desc' ? (
    <ChevronDown className="w-3 h-3 text-gold-700 ml-1 inline-block" />
  ) : (
    <ChevronUp className="w-3 h-3 text-gold-700 ml-1 inline-block" />
  );
}

// ─── Season Selector (custom, no native <select>) ────────────────────────────

function SeasonSelector({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (y: number) => void;
  options: number[];
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...options].sort((a, b) => b - a), [options]);
  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] border border-gold-100 rounded font-sans text-xs tracking-[0.1em] uppercase text-gold-950 bg-card hover:bg-gold-50 transition-colors"
        data-testid="season-selector-button"
      >
        <span>Season {value}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1 w-40 border border-gold-100 rounded bg-card shadow-raised z-10"
        >
          {sorted.map((y) => (
            <li key={y}>
              <button
                type="button"
                role="option"
                aria-selected={y === value}
                onClick={() => {
                  onChange(y);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2 min-h-[var(--touch-target-min)] font-sans text-xs tracking-[0.1em] uppercase transition-colors ${
                  y === value
                    ? 'bg-gold-950 text-gold-50'
                    : 'text-gold-950 hover:bg-gold-50'
                }`}
              >
                Season {y}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function InstagramMetricsPage() {
  // Season SSOT: seed the season selector from the active-season seam's
  // operative year rather than a clock-based `new Date().getFullYear()`.
  const { operativeYear } = useActiveSeason();
  const [competitionYear, setCompetitionYear] = useState<number>(operativeYear);
  const [entries, setEntries] = useState<SeasonLeaderboardEntry[]>([]);
  const [growthSeries, setGrowthSeries] = useState<GrowthSeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('followersGained');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [heroMetric, setHeroMetric] = useState<HeroMetric>('followersGained');

  // Available seasons for the selector — accumulate every year we encounter
  // in a fetch so prior seasons become visible after any refresh loads.
  const [knownSeasons, setKnownSeasons] = useState<Set<number>>(
    new Set([operativeYear]),
  );

  const fetchSeasonData = useCallback(
    async (year: number, metric: HeroMetric) => {
      setLoading(true);
      setError(null);

      // Dev-mock short-circuit.
      if (import.meta.env.DEV && USE_MOCK_SPOTLIGHT) {
        setEntries(MOCK_SEASON_LEADERBOARD.items as SeasonLeaderboardEntry[]);
        setGrowthSeries(MOCK_GROWTH_SERIES.items as GrowthSeriesItem[]);
        setKnownSeasons((prev) => new Set([...prev, year]));
        setLoading(false);
        // Suppress unused-warning for `metric` in mock mode.
        void metric;
        return;
      }

      try {
        const [boardRes, seriesRes] = await Promise.all([
          client.graphql({
            query: listSeasonLeaderboard,
            variables: { competitionYear: year, sortBy: 'followersGained' },
            authMode: 'userPool',
          }) as Promise<
            GraphQLResult<{
              listSeasonLeaderboard: {
                items: SeasonLeaderboardEntry[];
                nextToken: string | null;
              };
            }>
          >,
          client.graphql({
            query: listSeasonGrowthSeries,
            variables: { competitionYear: year, metric, limit: 24 },
            authMode: 'userPool',
          }) as Promise<
            GraphQLResult<{
              listSeasonGrowthSeries: { items: GrowthSeriesItem[] };
            }>
          >,
        ]);

        const items = boardRes.data?.listSeasonLeaderboard?.items ?? [];
        const series = seriesRes.data?.listSeasonGrowthSeries?.items ?? [];
        setEntries(items);
        setGrowthSeries(series);
        if (items.length > 0) {
          setKnownSeasons((prev) => new Set([...prev, year]));
        }
      } catch (err) {
        const msg =
          (err as Error)?.message ??
          'Unable to load the season leaderboard. Try again in a moment.';
        setError(msg);
        setEntries([]);
        setGrowthSeries([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void fetchSeasonData(competitionYear, heroMetric);
  }, [competitionYear, heroMetric, fetchSeasonData]);

  // Re-sort client-side to avoid a round-trip on header clicks.
  const sortedEntries = useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      const va = a[sortColumn] as number;
      const vb = b[sortColumn] as number;
      if (vb !== va) return sortDirection === 'desc' ? vb - va : va - vb;
      return a.cognitoSub.localeCompare(b.cognitoSub);
    });
    return sorted;
  }, [entries, sortColumn, sortDirection]);

  // Per-row sparkline lookup keyed by cognitoSub (stable across sort).
  const sparklineBySub = useMemo(() => {
    const m = new Map<string, SparklinePoint[]>();
    for (const s of growthSeries) m.set(s.cognitoSub, s.points);
    return m;
  }, [growthSeries]);

  const latestSnapshotDate = useMemo(() => {
    let latest: string | null = null;
    for (const e of entries) {
      if (!latest || (e.lastSnapshotAt && e.lastSnapshotAt > latest)) {
        latest = e.lastSnapshotAt;
      }
    }
    return latest;
  }, [entries]);

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (column === sortColumn) {
        setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortColumn(column);
        setSortDirection('desc');
      }
    },
    [sortColumn],
  );

  // Top-5 for the hero chart by the current hero metric. Because
  // listSeasonGrowthSeries already filters to top-N server-side when
  // `cognitoSubs` is omitted, we use the returned series directly.
  const heroTop5 = useMemo(() => growthSeries.slice(0, 5), [growthSeries]);

  // ── Loading State ──
  if (loading) {
    return (
      <div data-testid="instagram-metrics-page">
        <CrownLoader fullPage message="Loading insight…" />
      </div>
    );
  }

  return (
    <div data-testid="instagram-metrics-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl text-gold-950">Insight</h1>
          <p
            className="text-eyebrow-sm text-gold-400 mt-1"
            data-testid="last-snapshot-label"
          >
            {latestSnapshotDate
              ? `Last snapshot ${formatDate(latestSnapshotDate)}`
              : 'Season-scoped insights'}
          </p>
        </div>
        <SeasonSelector
          value={competitionYear}
          onChange={setCompetitionYear}
          options={Array.from(knownSeasons)}
        />
      </div>

      {/* Error State (inline panel, never a native alert) */}
      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 flex items-center gap-3 text-red-800 text-sm"
          role="alert"
          data-testid="insight-error"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!error && entries.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-20 space-y-4"
          data-testid="insight-empty"
        >
          <div className="w-16 h-16 rounded-full bg-gold-100 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-gold-400" />
          </div>
          <p className="font-display text-lg text-gold-700">
            No contestants approved for {competitionYear} yet
          </p>
          <p className="font-sans text-sm text-gold-500 max-w-md text-center">
            Season-scoped insights appear here once contestants are approved
            and their baseline is captured.
          </p>
        </div>
      )}

      {/* Hero chart + leaderboard (only when we have data) */}
      {!error && entries.length > 0 && (
        <>
          <SeasonGrowthHeroChart
            allSeries={heroTop5}
            metric={heroMetric}
            onMetricChange={setHeroMetric}
          />

          <DataTable
            ariaLabel="Season leaderboard"
            data-table-testid="season-leaderboard-table"
            rowKey={(entry) => entry.cognitoSub}
            rows={sortedEntries}
            rowTestId={(entry) => `season-row-${entry.cognitoSub}`}
            minWidth={1100}
            columns={[
              {
                id: 'rank',
                header: '#',
                width: '48px',
                renderCell: (_entry, idx) => (
                  <span className="font-display text-sm text-muted-foreground font-bold">
                    {idx + 1}
                  </span>
                ),
              },
              {
                id: 'contestant',
                header: 'Contestant',
                renderCell: (entry) => (
                  <span className="font-display text-sm text-foreground font-semibold">
                    {entry.fullName || '—'}
                  </span>
                ),
              },
              {
                id: 'instagram',
                header: 'Instagram',
                renderCell: (entry) => (
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <InstagramIcon className="text-muted-foreground" />
                    <span className="font-sans text-sm">@{entry.instagramUsername}</span>
                  </span>
                ),
                hideOnMobile: true,
              },
              {
                id: 'growth',
                header: 'Growth',
                renderCell: (entry) => (
                  <SeasonGrowthSparkline
                    key={entry.cognitoSub}
                    points={sparklineBySub.get(entry.cognitoSub) ?? []}
                    ariaLabel={`Growth sparkline for ${entry.fullName || entry.instagramUsername}`}
                  />
                ),
                hideOnMobile: true,
              },
              ...METRIC_COLUMNS.map((col) => ({
                id: col.key,
                align: 'right' as const,
                hideOnMobile: true,
                header: (
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    data-testid={`sort-${col.key}`}
                    className="inline-flex items-center justify-end gap-1 cursor-pointer hover:text-foreground transition-colors bg-transparent border-none text-eyebrow-xs text-muted-foreground touch-target"
                  >
                    {col.label}
                    <SortIndicator
                      column={col.key}
                      activeColumn={sortColumn}
                      direction={sortDirection}
                    />
                  </button>
                ),
                renderCell: (entry: typeof sortedEntries[number]) => (
                  <span className="font-sans text-sm text-foreground tabular-nums">
                    {formatNumber(entry[col.key as keyof typeof entry] as number)}
                  </span>
                ),
              })),
              {
                id: 'baseline',
                header: 'Baseline',
                renderCell: (entry) => (
                  <span className="font-sans text-xs text-muted-foreground tabular-nums">
                    {formatDate(entry.baselineCapturedAt)}
                  </span>
                ),
                hideOnMobile: true,
              },
              {
                id: 'lastSnapshot',
                header: 'Last Snapshot',
                renderCell: (entry) => (
                  <span className="font-sans text-xs text-muted-foreground tabular-nums">
                    {formatDate(entry.lastSnapshotAt)}
                  </span>
                ),
                hideOnMobile: true,
              },
            ]}
          />

          <p className="text-eyebrow-sm text-muted-foreground text-right">
            {entries.length} contestant{entries.length !== 1 ? 's' : ''} tracked
          </p>
        </>
      )}
    </div>
  );
}

export default InstagramMetricsPage;
